import XIcon from "@redotech/redo-icons/x-close.svg";
import classnames from "classnames";
import { memo } from "react";
import { Flex } from "../../flex";
import { Text } from "../../text";
import * as darkModeCss from "../../theme/component-colors-dark.module.css";
import { RedoBadge } from "../badge/redo-badge";
import { RedoButton } from "../buttons/redo-button";
import * as styles from "./redo-feature-notification-card.module.css";

export interface RedoFeatureNotificationCardProps {
  title: string;
  description: string;
  image?: string;
  actionText?: string;
  showBetaBadge?: boolean;
  onDismiss?: () => void;
  onAction?: () => void;
}

export const RedoFeatureNotificationCard = memo(
  function RedoFeatureNotificationCard({
    title,
    description,
    image,
    actionText = "Learn more",
    showBetaBadge = false,
    onDismiss,
    onAction,
  }: RedoFeatureNotificationCardProps) {
    const handleAction = () => {
      onAction?.();
    };

    return (
      <Flex
        bgColor="tertiary"
        borderColor="primary"
        borderStyle="solid"
        borderWidth="1px"
        className={classnames(styles.container, darkModeCss.darkMode)}
        data-arbiter="RedoFeatureNotificationCard"
        flexDirection="column"
        gap="xl"
        px="xl"
        py="2xl"
        radius="lg"
        shadow="lg"
      >
        <Flex flexDirection="column" gap="xs">
          <Flex flexDirection="row" gap="sm">
            <Text fontSize="sm" fontWeight="semibold" textColor="primary">
              {title}
            </Text>
            {showBetaBadge && <RedoBadge color="gray" size="xs" text="BETA" />}
          </Flex>
          <Text fontSize="sm" textColor="secondary">
            {description}
          </Text>
        </Flex>

        {image && (
          <Flex radius="md">
            <img alt={title} className={styles.image} src={image} />
          </Flex>
        )}

        <Flex flexDirection="row" gap="lg" justify="space-between">
          <RedoButton
            hierarchy="link"
            onClick={handleAction}
            text={actionText}
            theme="action"
          />
        </Flex>
        {onDismiss && (
          <Flex className={styles.closeX}>
            <RedoButton
              aria-label="Dismiss"
              IconLeading={XIcon}
              onClick={onDismiss}
              size="sm"
            />
          </Flex>
        )}
      </Flex>
    );
  },
);
